// A panel's selection (02-DESIGN/14-solutions/03-the-console.md § the
// elements: a panel's selection): carried in the address's query, drawn as
// chips under the title, each removable, and *Clear*; shared by a panel's own
// page and the tabs of Audit & usage.

import { useSearchParams } from "react-router";

import { pageOffered } from "../../roles/offers.ts";
import { Button } from "../../ui/components.tsx";
import { NameLink } from "../../ui/ident.tsx";
import type { Panel } from "../../wire/solutions.ts";
import { useSurface } from "../surface.tsx";
import { cleared, selectionOf, withSelected, type Selection } from "./panel.ts";
import { chipPath, type Linked } from "./who.ts";

/** usePanelSelection is a panel's selection from the address, a way to choose one field, and a way to clear them all. */
export function usePanelSelection(p: Panel | undefined): {
  selection: Selection;
  select: (field: string, value?: string, label?: string) => void;
  clear: () => void;
} {
  const [query, setQuery] = useSearchParams();
  return {
    selection: p === undefined ? {} : selectionOf(p, query),
    select: (field, value, label) => {
      setQuery(withSelected(query, field, value, label), { replace: true });
    },
    clear: () => {
      if (p !== undefined) {
        setQuery(cleared(p, query), { replace: true });
      }
    },
  };
}

/**
 * Chips is the panel's selection: one chip per field chosen, each removed on
 * its own, and *Clear*. A chip names the thing chosen, and where it has a
 * page the role is offered — an app's, a model's — the name links to it: a
 * selecting row's first cell is its opener, so the chip is the way there.
 */
export function Chips({
  p,
  selection,
  onRemove,
  onClear,
}: {
  p: Panel;
  selection: Selection;
  onRemove: (field: string) => void;
  onClear: () => void;
}) {
  const s = useSurface();
  const may = (page: Linked) => pageOffered(s.role, page);
  const set = (p.selection ?? []).filter((f) => selection[f.name] !== undefined);
  if (set.length === 0) {
    return null;
  }
  return (
    <div className="chips" aria-label="Narrowed to">
      {set.map((f) => (
        <span className="chip" key={f.name}>
          <span className="chip-field">{f.label}</span>
          <ChipName
            label={selection[f.name]?.label ?? ""}
            to={chipPath(p, f.name, selection[f.name]?.value ?? "", s.tenant, may)}
          />
          <button
            type="button"
            aria-label={`Remove ${f.label}`}
            onClick={() => {
              onRemove(f.name);
            }}
          >
            ×
          </button>
        </span>
      ))}
      <Button size="sm" kind="quiet" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}

function ChipName({ label, to }: { label: string; to?: string }) {
  return to === undefined ? <>{label}</> : <NameLink to={to}>{label}</NameLink>;
}
